import { useState, useEffect } from "react";
import { api } from "../api";
import { Loader2, Save, Plus, Bot, Trash2, Pencil, X, CheckCircle2, AlertTriangle } from "lucide-react";

interface Agent {
  id: number;
  name: string;
  description?: string;
  system_prompt: string;
  personality?: string;
  model: string;
  skills: string;
  tools: string;
  sub_agent_ids: string;
  is_default: boolean;
  is_shared: boolean;
}

interface Skill {
  name: string;
  title: string;
}

const PERSONALITY_PRESETS = [
  "",
  "Friendly and warm, like a helpful colleague. Use emojis occasionally and keep the tone light.",
  "Concise and direct. Get straight to the point with short answers.",
  "Formal and professional, like a senior analyst briefing a board.",
  "Patient and educational, explaining each step as if teaching a junior analyst.",
  "Enthusiastic and optimistic. Celebrate good results and encourage action.",
  "Skeptical and cautious. Always question assumptions and highlight risks.",
];

const AVAILABLE_TOOLS = [
  "list_available_data",
  "query_timeseries",
  "query_table",
  "get_kpis",
  "query_history",
  "run_data_query",
  "call_sub_agent",
  "list_sub_agents",
  "composio_execute",
  "composio_list_actions",
];

export default function Agents() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [editing, setEditing] = useState<Partial<Agent> | null>(null);
  const [isNew, setIsNew] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [a, s] = await Promise.all([api.listAgents(), api.listSkills()]);
      setAgents(a.agents || []);
      setSkills(s.skills || []);
    } catch (e) {
      setMessage({ type: "error", text: e instanceof Error ? e.message : "Could not load" });
    } finally {
      setLoading(false);
    }
  };

  const startNew = () => {
    setEditing({
      name: "",
      description: "",
      system_prompt: "You are a specialized assistant for NovaCare Health.",
      personality: "",
      model: "deepseek-v4-flash",
      skills: "[]",
      tools: "[]",
      sub_agent_ids: "[]",
      is_default: false,
      is_shared: true,
    });
    setIsNew(true);
  };

  const startEdit = (agent: Agent) => {
    setEditing({ ...agent });
    setIsNew(false);
  };

  const cancelEdit = () => {
    setEditing(null);
    setIsNew(false);
  };

  const saveAgent = async () => {
    if (!editing?.name || !editing.system_prompt) return;
    setSaving(true);
    setMessage(null);
    try {
      const payload = {
        name: editing.name,
        description: editing.description || "",
        system_prompt: editing.system_prompt,
        personality: editing.personality || "",
        model: editing.model || "deepseek-v4-flash",
        skills: JSON.parse(editing.skills || "[]"),
        tools: JSON.parse(editing.tools || "[]"),
        sub_agent_ids: JSON.parse(editing.sub_agent_ids || "[]"),
        is_default: !!editing.is_default,
        is_shared: editing.is_shared !== false,
      };
      if (isNew) {
        await api.createAgent(payload);
      } else if (editing.id) {
        await api.updateAgent(editing.id, payload);
      }
      await loadAll();
      setEditing(null);
      setIsNew(false);
      setMessage({ type: "success", text: isNew ? "Agent created." : "Agent updated." });
    } catch (e) {
      setMessage({ type: "error", text: e instanceof Error ? e.message : "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  const deleteAgent = async (id: number) => {
    if (!confirm("Delete this agent?")) return;
    setSaving(true);
    try {
      await api.deleteAgent(id);
      await loadAll();
      setMessage({ type: "success", text: "Agent deleted." });
    } catch (e) {
      setMessage({ type: "error", text: e instanceof Error ? e.message : "Delete failed" });
    } finally {
      setSaving(false);
    }
  };

  const toggleSkill = (skillName: string, checked: boolean) => {
    if (!editing) return;
    const list: string[] = JSON.parse(editing.skills || "[]");
    const next = checked ? [...new Set([...list, skillName])] : list.filter((n) => n !== skillName);
    setEditing({ ...editing, skills: JSON.stringify(next) });
  };

  const toggleTool = (tool: string, checked: boolean) => {
    if (!editing) return;
    const list: string[] = JSON.parse(editing.tools || "[]");
    const next = checked ? [...new Set([...list, tool])] : list.filter((t) => t !== tool);
    setEditing({ ...editing, tools: JSON.stringify(next) });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-novacare-700" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-slate-900 mb-2">AI Agents</h1>
      <p className="text-slate-500 mb-6">Create custom agents with specific prompts, skills and tools.</p>

      {message && (
        <div className={`flex items-center gap-2 rounded-lg px-4 py-3 text-sm mb-6 ${message.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
          {message.type === "success" ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      <section className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900">Custom Agents</h2>
          <button onClick={startNew} className="inline-flex items-center gap-1 text-sm text-novacare-700 hover:text-novacare-800 font-medium">
            <Plus className="w-4 h-4" /> New agent
          </button>
        </div>

        <div className="space-y-3">
          {agents.map((agent) => (
            <div key={agent.id} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
              <div className="flex-1 min-w-0 mr-4">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-slate-800">{agent.name}</p>
                  {agent.is_default && <span className="text-xs bg-novacare-100 text-novacare-700 px-2 py-0.5 rounded-full">Default</span>}
                </div>
                <p className="text-xs text-slate-500 truncate">{agent.description || agent.system_prompt.slice(0, 80)}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => startEdit(agent)} className="p-1.5 text-slate-400 hover:text-novacare-700 hover:bg-slate-100 rounded-lg transition-colors" title="Edit">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => deleteAgent(agent.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {agents.length === 0 && <p className="text-sm text-slate-400">No custom agents yet.</p>}
        </div>
      </section>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-900">{isNew ? "New Agent" : "Edit Agent"}</h3>
              <button onClick={cancelEdit} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                  <input type="text" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-novacare-700" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Model</label>
                  <input type="text" value={editing.model} onChange={(e) => setEditing({ ...editing, model: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-novacare-700" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <input type="text" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-novacare-700" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">System Prompt</label>
                <textarea value={editing.system_prompt} onChange={(e) => setEditing({ ...editing, system_prompt: e.target.value })} rows={8} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-novacare-700 text-sm font-mono" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Skills</label>
                <div className="flex flex-wrap gap-2">
                  {skills.map((s) => {
                    const selected = (JSON.parse(editing.skills || "[]") as string[]).includes(s.name);
                    return (
                      <label key={s.name} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs cursor-pointer border ${selected ? "bg-novacare-50 border-novacare-400 text-novacare-800" : "bg-white border-slate-200 text-slate-600"}`}>
                        <input type="checkbox" className="hidden" checked={selected} onChange={(e) => toggleSkill(s.name, e.target.checked)} />
                        {s.title || s.name}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tools</label>
                <div className="flex flex-wrap gap-2">
                  {AVAILABLE_TOOLS.map((t) => {
                    const selected = (JSON.parse(editing.tools || "[]") as string[]).includes(t);
                    return (
                      <label key={t} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs cursor-pointer border ${selected ? "bg-novacare-50 border-novacare-400 text-novacare-800" : "bg-white border-slate-200 text-slate-600"}`}>
                        <input type="checkbox" className="hidden" checked={selected} onChange={(e) => toggleTool(t, e.target.checked)} />
                        {t}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Sub-Agents</label>
                <div className="flex flex-wrap gap-2">
                  {agents.filter((a) => a.id !== editing.id).map((a) => {
                    const selected = (JSON.parse(editing.sub_agent_ids || "[]") as number[]).includes(a.id);
                    return (
                      <label key={a.id} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs cursor-pointer border ${selected ? "bg-novacare-50 border-novacare-400 text-novacare-800" : "bg-white border-slate-200 text-slate-600"}`}>
                        <input type="checkbox" className="hidden" checked={selected} onChange={(e) => {
                          const list: number[] = JSON.parse(editing.sub_agent_ids || "[]");
                          const next = e.target.checked ? [...new Set([...list, a.id])] : list.filter((id) => id !== a.id);
                          setEditing({ ...editing, sub_agent_ids: JSON.stringify(next) });
                        }} />
                        {a.name}
                      </label>
                    );
                  })}
                </div>
                {agents.filter((a) => a.id !== editing.id).length === 0 && <p className="text-xs text-slate-400">Create more agents to enable sub-agent calling.</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Personality</label>
                <select
                  value={editing.personality || ""}
                  onChange={(e) => setEditing({ ...editing, personality: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 mb-2 focus:ring-2 focus:ring-novacare-700"
                >
                  {PERSONALITY_PRESETS.map((preset, idx) => (
                    <option key={idx} value={preset}>
                      {idx === 0 ? "Default (no extra personality)" : preset.split(",").slice(0, 2).join(",") + "..."}
                    </option>
                  ))}
                </select>
                <textarea
                  value={editing.personality || ""}
                  onChange={(e) => setEditing({ ...editing, personality: e.target.value })}
                  rows={4}
                  placeholder="Describe the agent's tone, style and how it should feel in conversation..."
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-novacare-700 text-sm"
                />
                <p className="text-xs text-slate-500 mt-1">This is appended to the system prompt to shape the agent's tone, like ChatGPT's custom instructions.</p>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={!!editing.is_default} onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })} />
                  Default agent
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={editing.is_shared !== false} onChange={(e) => setEditing({ ...editing, is_shared: e.target.checked })} />
                  Shared with all users
                </label>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-100">
              <button onClick={cancelEdit} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Cancel</button>
              <button onClick={saveAgent} disabled={saving || !editing.name} className="inline-flex items-center gap-2 px-4 py-2 bg-novacare-700 hover:bg-novacare-800 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Agent
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
